import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import type { NotificationDto } from '@mesterplan/api-client';
import { useNetUser } from '@/hooks/useNetUser';
import { NotificationType } from '@mesterplan/shared-types';

const NOTIFICATIONS_CACHE_KEY = 'USER_NOTIFICATIONS';

/**
 * In-app notification feed, served by the .NET API (`/api/notifications/*`). Replaces the
 * direct Firestore `NOTIFICATION_LOGS` reads/writes the panel used to perform. The two-bucket
 * merge (active-org rows + cross-org partner rows) and the unread count are now computed
 * server-side; this hook is a thin react-query wrapper that polls on the same cadence the
 * legacy poll-based implementation used (the panel was already poll-driven, not live).
 *
 * Note: nothing in the .NET backend writes these rows yet — producers are still the Firebase
 * Cloud Functions under `functions/src/notifications/*`. This is a read/mark cutover only.
 */

/**
 * UI-facing notification shape. Mirrors the legacy Firestore document but with ISO-8601 string
 * timestamps (the panel formats `sentAt` itself). `data` carries the navigation payload the
 * panel reads for click-through (creditorInvoiceId / inviteToken / partnerEmail / entityType /
 * entityId / projectId).
 */
export interface NotificationLog {
    id: string;
    userId: string;
    companyId?: string | null;
    type: NotificationType;
    title: string;
    body: string;
    data?: Record<string, unknown>;
    sentAt: string;
    readAt?: string | null;
    clickedAt?: string | null;
    deliveryStatus: 'sent' | 'delivered' | 'failed';
    errorMessage?: string | null;
}

function toNotificationLog(dto: NotificationDto): NotificationLog {
    return {
        id: dto.id,
        userId: dto.userId,
        companyId: dto.companyId ?? null,
        type: dto.type as NotificationType,
        title: dto.title,
        body: dto.body,
        data: dto.data,
        sentAt: dto.sentAt,
        readAt: dto.readAt ?? null,
        clickedAt: dto.clickedAt ?? null,
        deliveryStatus: dto.deliveryStatus,
        errorMessage: dto.errorMessage ?? null,
    };
}

export function useNotificationOperations() {
    const api = useMesterplanApi();
    const { user } = useNetUser();
    const queryClient = useQueryClient();

    const userId = user?.id ?? null;
    const activeOrgId = user?.activeOrgId ?? null;

    /**
     * Fetch notifications for the current user. The .NET API merges the active-org and
     * cross-org partner buckets and returns them newest-first.
     */
    const useFetchUserNotifications = (maxCount = 20) => {
        return useQuery({
            queryKey: [NOTIFICATIONS_CACHE_KEY, userId, activeOrgId, maxCount],
            queryFn: async (): Promise<NotificationLog[]> => {
                const result = await api.notifications.list({ maxCount });
                if (!result.ok) {
                    throw new Error(result.error?.detail ?? result.error?.title ?? 'Kunne ikke hente notifikationer');
                }
                return result.data.map(toNotificationLog);
            },
            enabled: !!userId,
            staleTime: 30000,
            refetchInterval: 60000,
        });
    };

    /**
     * Get unread notification count (server-computed across both buckets).
     */
    const useUnreadNotificationCount = () => {
        return useQuery({
            queryKey: [NOTIFICATIONS_CACHE_KEY, 'unread_count', userId, activeOrgId],
            queryFn: async (): Promise<number> => {
                const result = await api.notifications.unreadCount();
                if (!result.ok) {
                    throw new Error(result.error?.detail ?? result.error?.title ?? 'Kunne ikke hente antal');
                }
                return result.data;
            },
            enabled: !!userId,
            staleTime: 30000,
            refetchInterval: 60000,
        });
    };

    /**
     * Mark a notification as read.
     */
    const useMarkNotificationRead = () => {
        return useMutation({
            mutationFn: async (notificationId: string) => {
                const result = await api.notifications.markRead(notificationId);
                if (!result.ok) {
                    throw new Error(result.error?.detail ?? result.error?.title ?? 'Kunne ikke markere som læst');
                }
                return notificationId;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [NOTIFICATIONS_CACHE_KEY],
                });
            },
        });
    };

    /**
     * Mark all notifications as read. Accepts the explicit set of currently-unread ids.
     */
    const useMarkAllNotificationsRead = () => {
        return useMutation({
            mutationFn: async (notificationIds: string[]) => {
                if (notificationIds.length === 0) return;
                const result = await api.notifications.markAllRead({ ids: notificationIds });
                if (!result.ok) {
                    throw new Error(result.error?.detail ?? result.error?.title ?? 'Kunne ikke markere alle som læst');
                }
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [NOTIFICATIONS_CACHE_KEY],
                });
            },
        });
    };

    /**
     * Delete notifications for a specific creditor invoice (cleanup when an e-faktura is handled).
     * A failure here must not block the calling flow, matching the legacy behaviour.
     */
    const useDeleteCreditorInvoiceNotifications = () => {
        return useMutation({
            mutationFn: async (creditorInvoiceId: string) => {
                const result = await api.notifications.deleteForCreditorInvoice({ creditorInvoiceId });
                if (!result.ok) {
                    // Cleanup operation — log and swallow so it never blocks the main flow.
                    console.error('Error deleting creditor invoice notifications:', result.error);
                }
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [NOTIFICATIONS_CACHE_KEY],
                });
            },
        });
    };

    return {
        useFetchUserNotifications,
        useUnreadNotificationCount,
        useMarkNotificationRead,
        useMarkAllNotificationsRead,
        useDeleteCreditorInvoiceNotifications,
    };
}
